import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useChatIdentityKey } from '@/api/chat/use-chat-identity-key';
import { useCommunicationsShellSlice } from '@/stores/selectors';

import type { KindFilter, RightPaneMode } from '../types';
import { parseIdParam, parseKindParam, parsePaneParam } from '../utils';

export const SHELL_PARAM = {
  kind: 'kind',
  context: 'ctx',
  thread: 'thread',
  pane: 'open',
} as const;

export interface ShellUrlValues {
  kind: KindFilter;
  contextId: string | null;
  threadId: string | null;
  pane: RightPaneMode | null;
}

export type ShellUrlPatch = Partial<ShellUrlValues>;

export interface ShellUrlSeed {
  kind?: KindFilter;
  contextId?: string;
  threadId?: string;
}

export interface ShellUrlState extends ShellUrlValues {
  hasStaleSelection: boolean;
  patch(_next: ShellUrlPatch, _history?: 'push' | 'replace'): void;
}

const isDefault = (key: keyof ShellUrlValues, value: unknown) =>
  value === null || value === undefined || (key === 'kind' && value === 'all');

export const useShellUrlState = (seed: ShellUrlSeed = {}): ShellUrlState => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { key: locationKey } = useLocation();
  const identity = useChatIdentityKey();
  const { entryIdentities } = useCommunicationsShellSlice();
  const ranAs = entryIdentities[locationKey];
  const isLeftover = ranAs !== undefined && ranAs !== identity;

  const values = useMemo(() => {
    const contextId = parseIdParam(searchParams.get(SHELL_PARAM.context));
    const threadId = parseIdParam(searchParams.get(SHELL_PARAM.thread));
    const pane = parsePaneParam(searchParams.get(SHELL_PARAM.pane));
    const hasSelection = contextId !== null || threadId !== null || pane !== null;

    return {
      kind: parseKindParam(searchParams.get(SHELL_PARAM.kind)),
      contextId: isLeftover ? null : contextId,
      threadId: isLeftover ? null : threadId,
      pane: isLeftover ? null : pane,
      hasStaleSelection: isLeftover && hasSelection,
    };
  }, [isLeftover, searchParams]);

  const patch = useCallback(
    (next: ShellUrlPatch, history: 'push' | 'replace' = 'replace') => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          (Object.entries(next) as Array<[keyof ShellUrlValues, unknown]>).forEach(
            ([key, value]) => {
              const param =
                SHELL_PARAM[key === 'contextId' ? 'context' : key === 'threadId' ? 'thread' : key];

              if (isDefault(key, value)) params.delete(param);
              else params.set(param, String(value));
            },
          );

          // Returning `prev` untouched keeps React Router from recording a no-op entry, which
          // is what would otherwise make Back appear to do nothing on a repeated click.
          return params.toString() === prev.toString() ? prev : params;
        },
        { replace: history === 'replace' },
      );
    },
    [setSearchParams],
  );

  const { kind: seedKind, contextId: seedContextId, threadId: seedThreadId } = seed;

  useEffect(() => {
    const pending: ShellUrlPatch = {};

    if (seedKind && seedKind !== 'all' && !searchParams.has(SHELL_PARAM.kind)) {
      pending.kind = seedKind;
    }
    if (seedContextId && !searchParams.has(SHELL_PARAM.context)) {
      pending.contextId = seedContextId;
    }
    if (seedThreadId && !searchParams.has(SHELL_PARAM.thread)) {
      pending.threadId = seedThreadId;
    }

    if (Object.keys(pending).length > 0) patch(pending, 'replace');
    // Seeds once from the props the shell was mounted with; `searchParams` is read, not
    // depended on, or clearing a seeded context would immediately re-seed it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKind, seedContextId, seedThreadId, patch]);

  return { ...values, patch };
};
